import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Pagination,
} from '@mui/material';
import { Trash2 } from 'lucide-react';
import { toast } from '../../../../../../../ui/toast';
import { Dialog, Drawer } from '../../../../../../../ui';
import { deleteAssessmentTemplate, getAssessmentTemplateList } from '../../../../../../../utils/api_service/assessment_api';
import { AssessmentTemplateRequest, SimplifiedTemplate } from '../../../../../../../utils/types/assessment_types';
import { DIALOG_TYPE } from '../../../../../Constants/DialogTypes';

interface TemplateDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectTemplate: (template: SimplifiedTemplate) => void;
}

export const TemplateDrawer: React.FC<TemplateDrawerProps> = ({
  isOpen,
  onClose,
  onSelectTemplate,
}) => {
  const [templates, setTemplates] = useState<SimplifiedTemplate[]>([]);
  const [templateSearch, setTemplateSearch] = useState<string>('');
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [totalCount, setTotalCount] = useState<number>(1);
  const [deleteTemplate, setDeleteTemplate] = useState<string>('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const itemsPerPage = 8;

  const totalPages = Math.ceil(totalCount / itemsPerPage);

  const getAssessmentTemplates = async () => {
    const requestBody: AssessmentTemplateRequest = {
      jobTitle: '',
      templateName: templateSearch,
    };

    try {
      const response = await getAssessmentTemplateList(requestBody, pageNumber, itemsPerPage);
      if (response.ok) {
        const data = await response.json();
        const formattedTemplates: SimplifiedTemplate[] = data.results.map(
          (item: any) => ({
            templateId: item.id,
            templateTitle: item.templateTitle,
            jobTitle: item.jobTitle?.jobTitle ?? 'N/A',
            createdAt: item.createdAt,
            questionCount: item.questionAnswer?.length ?? 0,
          }),
        );

        setPageNumber(data.page);
        setTotalCount(data.total_count);

        setTemplates(formattedTemplates);
      } else {
        toast.error('Filter not updated', { position: 'bottom-center' });
      }
    } catch {
      toast.error('Filter not updated', { position: 'bottom-center' });
    }
  };

  useEffect(() => {
    getAssessmentTemplates();
  }, []);

  useEffect(() => {
    setPageNumber(1);
  }, [templateSearch]);

  useEffect(() => {
    const debounceSearchTerm = setTimeout(() => {
      getAssessmentTemplates();
    }, 200);

    return () => clearTimeout(debounceSearchTerm);
  }, [templateSearch, pageNumber]);

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleDeleteTemplate = async () => {
    if (!deleteTemplate) return;

    try {
      const response = await deleteAssessmentTemplate(deleteTemplate);

      if (response.ok) {
        await response.json();

        setDeleteTemplate('');
        setIsModalOpen(false);

        await getAssessmentTemplates();

        toast.success('Template deleted successfully!', {
          position: 'bottom-center',
        });
      } else {
        toast.error('Failed to delete template', { position: 'bottom-center' });
      }
    } catch {
      toast.error('Error deleting template', { position: 'bottom-center' });
    }
  };

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title="Assessment Templates"
      width="w-full md:w-[800px]"
    >
      <Box sx={{ p: { xs: 0.5, sm: 1.5 } }}>
        <Box sx={{ mb: { xs: 2, sm: 3 } }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search templates..."
            value={templateSearch}
            onChange={(e) => setTemplateSearch(e.target.value)}
          />
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1.5, sm: 2 } }}>
          {templates.map((template) => (
            <Paper
              key={template.templateId}
              variant="outlined"
              onClick={() => onSelectTemplate(template)}
              sx={{
                position: 'relative',
                p: { xs: 1.5, sm: 2 },
                borderRadius: 2,
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                  boxShadow: 2,
                  borderColor: 'primary.light',
                },
              }}
            >
              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={<Trash2 size={14} />}
                onClick={(e) => {
                  e.stopPropagation();
                  setDeleteTemplate(template.templateId);
                  setIsModalOpen(true);
                }}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  textTransform: 'none',
                  borderRadius: 4,
                  fontSize: '11px',
                  py: 0.25,
                  px: 1,
                }}
              >
                Delete
              </Button>

              <Box sx={{ mb: 1, pr: 10 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  {template.templateTitle}
                </Typography>
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {template.jobTitle}
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: 'space-between',
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  gap: 1.5,
                  pt: 1,
                  borderTop: 1,
                  borderColor: 'divider',
                }}
              >
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    Created at: {new Date(template.createdAt * 1000).toLocaleDateString('en-GB')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {template.questionCount} questions
                  </Typography>
                </Box>
                <Button
                  size="small"
                  variant="contained"
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelectTemplate(template);
                  }}
                  sx={{
                    textTransform: 'none',
                    borderRadius: 4,
                    fontSize: '12px',
                    width: { xs: '100%', sm: 'auto' },
                  }}
                >
                  Load Template
                </Button>
              </Box>
            </Paper>
          ))}

          {totalPages > 1 && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', sm: 'center' },
                gap: 2,
                borderTop: 1,
                borderColor: 'divider',
                pt: 2,
                mt: 2,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Showing {(pageNumber - 1) * itemsPerPage + 1} to{' '}
                {Math.min(pageNumber * itemsPerPage, totalCount)} of {totalCount} results
              </Typography>
              <Pagination
                count={totalPages}
                page={pageNumber}
                onChange={(_, page) => setPageNumber(page)}
                color="primary"
                size="small"
              />
            </Box>
          )}

          <Dialog
            isOpen={isModalOpen}
            onClose={handleCloseModal}
            title="Are you sure you want to delete this template?"
            dialogType={DIALOG_TYPE.ERROR}
            justify="center"
            confirmButtonLable="Delete Template"
            cancelButtonLable="Cancel"
            handleApply={handleDeleteTemplate}
          >
            <Typography variant="body2" sx={{ color: '#4b5563', textAlign: 'center', mt: 1 }}>
              This action cannot be undone.
            </Typography>
          </Dialog>
        </Box>
      </Box>
    </Drawer>
  );
};
export default TemplateDrawer;
